import {Box, Divider, Typography} from '@mui/material'
import {useLocation, useParams} from 'react-router-dom'
import {useRef, useState, useEffect} from 'react'
import {useGetNotification, useGetOrder} from '../../../logic/useApiQuery'
import { useReplyNotification, useMarkReplyRead } from '../../../logic/useApiMutation'
import { useOptimisticUpdateNotification, useOptimisticUpdateOrderNotification } from '../../../logic/optimisticUpdates'
import {LoadingScreen} from '../../shared'
import {PortalNotification, PortalNotificationEvent} from '../../../types'
import {hasUnreadReplies} from '../../../utils'
import dayjs from 'dayjs'
import {MessageBubble} from './MessageBubble'
import {ReplyAttachmentUploader, AttachmentState} from './ReplyAttachmentUploader'
import {ReplyForm} from './ReplyForm'

export function MessageThreadPage() {
  const { closingId, notificationId: notificationIdParam, id } = useParams()
  const notificationId = notificationIdParam ?? id
  const {search} = useLocation()
  const [replyText, setReplyText] = useState('')
  const [attachment, setAttachment] = useState<AttachmentState>({status: 'idle'})

  const {data: orderData} = useGetOrder(closingId ?? '', search ?? '')
  const CompanyIdOrderIdHash = orderData?.Order?.CompanyIdOrderIdHash ?? ''
  const cachedRef = orderData?.Notifications?.find(n => n?.NotificationId === notificationId)

  const placeholderData: PortalNotification | undefined = cachedRef
    ? {
      Title: cachedRef.NotificationTitle,
      CreatedDate: cachedRef.CreatedDateTime,
      CreatedDateTime: cachedRef.CreatedDateTime,
      Content: cachedRef.Content ?? '',
      NotificationId: cachedRef.NotificationId,
      ReadReceipt: cachedRef.ReadReceipt,
      Type: cachedRef.Type,
      Replies: cachedRef.Replies,
      UserOrderKey: '',
      CommunicationId: '',
      ProcessIdStepIdCommunicationId: '',
      CompanyId: '',
      OrderId: '',
      Role: '',
    }
    : undefined

  const {data: notification, isLoading, error} = useGetNotification({
    notificationId,
    queryOptions: {placeholderData},
  })

  const {optimisticUpdateNotification} = useOptimisticUpdateNotification()
  const {optimisticUpdateOrderNotification} = useOptimisticUpdateOrderNotification()

  const {mutate: markReplyRead} = useMarkReplyRead(
    closingId ?? '',
    notificationId ?? '',
    search,
    {
      onSuccess: () => {
        const withRepliesMarkedRead = (current: {Replies?: PortalNotificationEvent[]}) => ({
          ReadReceipt: true,
          Replies: (current.Replies ?? []).map(reply =>
            reply.Source === 'liveLook'
              ? {...reply, AdditionalData: {...reply.AdditionalData, readStatus: true}}
              : reply
          ),
        })
        optimisticUpdateNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: withRepliesMarkedRead,
        })
        optimisticUpdateOrderNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: withRepliesMarkedRead,
        })
      },
    },
  )

  // Mark as read on mount if not already read, or if there are unread replies. Use a ref so we
  // only fire once even if the notification data changes (e.g. placeholder → real data).
  const markedReadRef = useRef(false)
  useEffect(() => {
    if (!markedReadRef.current && notification && (!notification.ReadReceipt || hasUnreadReplies(notification))) {
      markedReadRef.current = true
      markReplyRead(undefined)
    }
  }, [notification, markReplyRead])

  const {mutate: sendReply, isLoading: isSending, error: sendError} = useReplyNotification(
    closingId ?? '',
    notificationId ?? '',
    search,
    {
      onSuccess: (_data, variables) => {
        const optimisticReply: PortalNotificationEvent = {
          UserOrderKeyNotificationId: '',
          CreatedDate: new Date().toISOString(),
          AuthProviderSub: '',
          CompanyIdOrderIdHash: '',
          NotificationId: notificationId ?? '',
          Type: 'read',
          UserOrderKey: '',
          AdditionalData: {
            message: variables.message,
            ...(variables.documentFileName ? {
              documentFileName: variables.documentFileName,
            } : {}),
          },
        }
        const appendReply = (current: {Replies?: PortalNotificationEvent[]}) => ({
          Replies: [...(current.Replies ?? []), optimisticReply],
        })
        optimisticUpdateNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: appendReply,
        })
        optimisticUpdateOrderNotification({
          orderKey: closingId ?? '',
          notificationId: notificationId ?? '',
          search,
          patch: appendReply,
        })
        setReplyText('')
        setAttachment({status: 'idle'})
      },
    },
  )

  const replies = notification?.Replies ?? []
  const hasReadyAttachment = attachment.status === 'ready'

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault()
    const message = replyText.trim()
    if (!message || isSending || attachment.status === 'uploading') return
    sendReply({
      message,
      ...(hasReadyAttachment ? {
        documentFileName: attachment.documentFileName,
      } : {}),
    })
  }

  if (isLoading) return <LoadingScreen/>
  if (error) throw error
  if (!notification) return null

  const baseDate = dayjs(notification.CreatedDate).format('MMMM D, YYYY [at] h:mm A')

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '750px'}}>
      <Typography
        variant="h5"
        sx={{fontWeight: 500, lineHeight: '22px', mb: 2, wordBreak: 'break-word'}}
      >
        {notification.Title}
      </Typography>
      <Divider sx={{mb: 3}}/>

      {/* Messages */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pb: '80px'}}>
        {notification.Content && (
          <MessageBubble
            content={notification.Content}
            date={baseDate}
            isOurs={false}
            allowHtml
            companyIdOrderIdHash={CompanyIdOrderIdHash}
          />
        )}

        {replies.map((reply) => (
          <MessageBubble
            key={reply.CreatedDate}
            content={typeof reply.AdditionalData?.message === 'string' ? reply.AdditionalData.message : ''}
            date={dayjs(reply.CreatedDate).format('MMMM D, YYYY [at] h:mm A')}
            isOurs={reply.Source !== 'liveLook'}
            isRead={reply.AdditionalData?.readStatus === true}
            documentFileName={typeof reply.AdditionalData?.documentFileName === 'string' ? reply.AdditionalData.documentFileName : undefined}
            documentDeleted={reply.AdditionalData?.documentDeleted === true}
            companyIdOrderIdHash={CompanyIdOrderIdHash}
          />
        ))}
      </Box>

      {attachment.status === 'uploading' && (
        <ReplyAttachmentUploader
          file={attachment.file}
          companyIdOrderIdHash={CompanyIdOrderIdHash}
          onUploaded={result => setAttachment({status: 'ready', file: attachment.file, ...result})}
          onError={message => setAttachment({status: 'error', file: attachment.file, message})}
        />
      )}

      <ReplyForm
        replyText={replyText}
        setReplyText={setReplyText}
        isSending={isSending}
        sendError={sendError}
        attachment={attachment}
        setAttachment={setAttachment}
        onSubmit={handleSubmit}
      />
    </Box>
  )
}
